import React, { useState } from 'react';
import { Table, Card, Button, Space, Modal, Form, Input, InputNumber, Switch, DatePicker, Tabs, message, Popconfirm, Tag, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FilePdfOutlined, UploadOutlined, PictureOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { MediaPickerModal } from '../../components/common/MediaPickerModal';

interface NewsletterTranslation {
    title: string;
    description?: string;
}

interface NewsletterItem {
    id: string;
    title: string;
    fileUrl: string;
    publishedDate: string;
    publishStartDate?: string;
    publishEndDate?: string;
    orderIndex: number;
    isActive: boolean;
    translations: Record<string, NewsletterTranslation>;
}

const appLanguages = [
    { code: 'de', label: 'Deutsch' },
    { code: 'en', label: 'English' },
    { code: 'tr', label: 'Türkçe' },
    { code: 'ru', label: 'Русский' },
    { code: 'ar', label: 'العربية' }
];

export const NewslettersList: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<NewsletterItem | null>(null);
    const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: newsletters = [], isLoading } = useQuery<NewsletterItem[]>({
        queryKey: ['adminNewsletters'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/newsletters');
            return response.data.data || [];
        }
    });

    const createMutation = useMutation({
        mutationFn: async (payload: any) => {
            const response = await apiClient.post('/admin/newsletters', payload);
            return response.data;
        },
        onSuccess: () => {
            message.success('Newsletter erfolgreich erstellt');
            queryClient.invalidateQueries({ queryKey: ['adminNewsletters'] });
            setIsModalOpen(false);
            form.resetFields();
        },
        onError: (err: any) => {
            message.error(err.response?.data?.message || 'Fehler beim Erstellen des Newsletters');
        }
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: any }) => {
            const response = await apiClient.put(`/admin/newsletters/${id}`, payload);
            return response.data;
        },
        onSuccess: () => {
            message.success('Newsletter erfolgreich aktualisiert');
            queryClient.invalidateQueries({ queryKey: ['adminNewsletters'] });
            setIsModalOpen(false);
            setEditingItem(null);
            form.resetFields();
        },
        onError: (err: any) => {
            message.error(err.response?.data?.message || 'Fehler beim Aktualisieren des Newsletters');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await apiClient.delete(`/admin/newsletters/${id}`);
        },
        onSuccess: () => {
            message.success('Newsletter gelöscht');
            queryClient.invalidateQueries({ queryKey: ['adminNewsletters'] });
        },
        onError: (err: any) => {
            message.error(err.response?.data?.message || 'Fehler beim Löschen');
        }
    });

    const handleOpenModal = (record?: NewsletterItem) => {
        if (record) {
            setEditingItem(record);
            const translations: Record<string, any> = {};
            appLanguages.forEach(lang => {
                translations[lang.code] = record.translations?.[lang.code] || { title: '', description: '' };
            });

            form.setFieldsValue({
                fileUrl: record.fileUrl,
                publishedDate: record.publishedDate ? dayjs(record.publishedDate) : dayjs(),
                orderIndex: record.orderIndex,
                isActive: record.isActive,
                translations
            });
        } else {
            setEditingItem(null);
            form.resetFields();
            form.setFieldsValue({
                publishedDate: dayjs(),
                orderIndex: newsletters.length + 1,
                isActive: true,
                translations: {
                    de: { title: '', description: '' }
                }
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (values: any) => {
        const translations: Record<string, any> = {};
        appLanguages.forEach(lang => {
            const t = values.translations?.[lang.code];
            if (t?.title?.trim()) {
                translations[lang.code] = {
                    title: t.title.trim(),
                    description: t.description?.trim() || null
                };
            }
        });

        // Ensure DE translation title exists
        if (!translations['de']?.title) {
            message.error('Deutscher Titel (DE) ist erforderlich.');
            return;
        }

        const payload = {
            id: editingItem ? editingItem.id : undefined,
            fileUrl: values.fileUrl,
            publishedDate: values.publishedDate ? values.publishedDate.toISOString() : new Date().toISOString(),
            publishStartDate: null,
            publishEndDate: null,
            orderIndex: values.orderIndex ?? 0,
            isActive: values.isActive ?? true,
            translations
        };

        if (editingItem) {
            updateMutation.mutate({ id: editingItem.id, payload });
        } else {
            createMutation.mutate(payload);
        }
    };

    const handleFileUpload = async (file: File) => {
        setUploadingPdf(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPublic', 'true');
        formData.append('isActive', 'true');

        try {
            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const dataObj = response.data?.data || response.data?.Data || response.data;
            const pdfUrl = dataObj?.url || dataObj?.Url || dataObj?.filePath || dataObj?.FilePath;
            if (pdfUrl) {
                form.setFieldValue('fileUrl', pdfUrl);
                message.success('PDF erfolgreich hochgeladen');
            } else {
                message.error('Keine URL in der Antwort erhalten');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Fehler beim Hochladen der PDF-Datei');
        } finally {
            setUploadingPdf(false);
        }
        return false;
    };

    const columns = [
        {
            title: 'Titel (DE)',
            dataIndex: 'title',
            key: 'title',
            render: (text: string, record: NewsletterItem) => (
                <Space>
                    <FilePdfOutlined className="text-red-500 text-lg" />
                    <div>
                        <div className="font-semibold text-gray-800">{text}</div>
                        {record.translations && Object.keys(record.translations).length > 1 && (
                            <div className="text-xs text-gray-400">
                                {Object.keys(record.translations).map(code => (
                                    <span key={code} className="mr-1 uppercase font-mono">[{code}]</span>
                                ))}
                            </div>
                        )}
                    </div>
                </Space>
            )
        },
        {
            title: 'Veröffentlichungsdatum',
            dataIndex: 'publishedDate',
            key: 'publishedDate',
            render: (val: string) => val ? dayjs(val).format('DD.MM.YYYY') : '-'
        },
        {
            title: 'Reihenfolge',
            dataIndex: 'orderIndex',
            key: 'orderIndex',
            width: 100
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (active: boolean) => (
                <Tag color={active ? 'green' : 'red'}>
                    {active ? 'Aktiv' : 'Inaktiv'}
                </Tag>
            )
        },
        {
            title: 'PDF Dokument',
            dataIndex: 'fileUrl',
            key: 'fileUrl',
            render: (url: string) => (
                <a href={resolveAssetUrl(url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                    <FilePdfOutlined /> PDF öffnen
                </a>
            )
        },
        {
            title: 'Aktionen',
            key: 'actions',
            width: 140,
            render: (_: any, record: NewsletterItem) => (
                <Space>
                    <Tooltip title="Bearbeiten">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleOpenModal(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Newsletter löschen?"
                        description="Möchten Sie diesen Newsletter wirklich löschen?"
                        onConfirm={() => deleteMutation.mutate(record.id)}
                        okText="Ja, löschen"
                        cancelText="Abbrechen"
                        okButtonProps={{ danger: true }}
                    >
                        <Tooltip title="Löschen">
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const tabItems = appLanguages.map(lang => ({
        key: lang.code,
        label: `${lang.label} (${lang.code.toUpperCase()})`,
        children: (
            <div className="py-2 space-y-4">
                <Form.Item
                    name={['translations', lang.code, 'title']}
                    label={`Titel (${lang.code.toUpperCase()})`}
                    rules={lang.code === 'de' ? [{ required: true, message: 'Deutscher Titel ist erforderlich' }] : []}
                >
                    <Input placeholder={`z.B. 1. Halbjahres-Newsletter ${new Date().getFullYear()}`} />
                </Form.Item>
                <Form.Item
                    name={['translations', lang.code, 'description']}
                    label={`Kurzbeschreibung (${lang.code.toUpperCase()})`}
                >
                    <Input.TextArea rows={2} placeholder="Optionale kurze Zusammenfassung des Inhalts..." />
                </Form.Item>
            </div>
        )
    }));

    return (
        <div className="p-6">
            <Card
                title={
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-lg font-bold">E-Bültenler (Newsletters)</span>
                            <span className="text-xs text-gray-500 block">
                                Halbjahres-, Sonder- und regelmäßige Newsletter verwalten. Die neuesten Einträge werden auf der Startseite und in den yapışkan/sticky Buttons angezeigt.
                            </span>
                        </div>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenModal()}
                        >
                            Neuer Newsletter
                        </Button>
                    </div>
                }
            >
                <Table
                    dataSource={newsletters}
                    columns={columns}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            {/* Create / Edit Modal */}
            <Modal
                title={editingItem ? 'Newsletter bearbeiten' : 'Neuen Newsletter anlegen'}
                open={isModalOpen}
                onCancel={() => { setIsModalOpen(false); setEditingItem(null); }}
                onOk={() => form.submit()}
                confirmLoading={createMutation.isPending || updateMutation.isPending}
                width={700}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Tabs items={tabItems} type="card" className="mb-4" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            name="publishedDate"
                            label="Veröffentlichungsdatum"
                            rules={[{ required: true, message: 'Datum erforderlich' }]}
                        >
                            <DatePicker className="w-full" format="DD.MM.YYYY" />
                        </Form.Item>

                        <Form.Item
                            name="orderIndex"
                            label="Reihenfolge-Index"
                        >
                            <InputNumber className="w-full" min={1} />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="fileUrl"
                        label="PDF Datei URL"
                        rules={[{ required: true, message: 'Bitte laden Sie eine PDF-Datei hoch oder wählen Sie eine aus' }]}
                    >
                        <Input placeholder="/media/public/documents/newsletter.pdf" />
                    </Form.Item>

                    <div className="flex items-center gap-3 mb-6">
                        <input
                            type="file"
                            id="newsletter-pdf-upload"
                            accept=".pdf,application/pdf"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileUpload(file);
                            }}
                        />
                        <Button
                            icon={<UploadOutlined />}
                            loading={uploadingPdf}
                            onClick={() => document.getElementById('newsletter-pdf-upload')?.click()}
                        >
                            PDF hochladen
                        </Button>
                        <Button
                            icon={<PictureOutlined />}
                            onClick={() => setIsMediaPickerOpen(true)}
                        >
                            Aus Mediathek wählen
                        </Button>
                    </div>

                    <Form.Item name="isActive" label="Aktiv (Öffentlich sichtbar)" valuePropName="checked">
                        <Switch checkedChildren="Aktiv" unCheckedChildren="Inaktiv" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Media Picker Modal */}
            <MediaPickerModal
                open={isMediaPickerOpen}
                onCancel={() => setIsMediaPickerOpen(false)}
                onSelect={(url) => {
                    form.setFieldValue('fileUrl', url);
                    setIsMediaPickerOpen(false);
                    message.success('Datei aus Mediathek ausgewählt');
                }}
            />
        </div>
    );
};

export default NewslettersList;
